import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Star, CheckCircle2, Quote } from "lucide-react";
import { SERVICES, BRAND } from "@/lib/site";
import { Reveal } from "@/components/motion-primitives";
import { SectionHead, Field, Input, Textarea, SubmitButton, usePageMeta } from "@/components/shared";
import { api, formatApiError } from "@/lib/api";

const REVIEW_SERVICES = [
  ...SERVICES.map((s) => s.title),
  "Home Cleaning",
  "Guest Cleaning / Stayover Refresh",
  "The Bermuda Vacation Rental Blueprint",
  "The Bermuda Co-Host Circle",
  "Other",
];

export const Stars = ({ value, size = 15 }) => (
  <span className="inline-flex gap-0.5" aria-label={`${value} out of 5 stars`}>
    {[1, 2, 3, 4, 5].map((s) => (
      <Star key={s} size={size} className={s <= value ? "fill-bermuda-dark text-bermuda-dark" : "text-navy/20"} />
    ))}
  </span>
);

export default function Reviews() {
  usePageMeta("Leave a Review", "Share your experience with The Bermuda Co-Host — co-hosting, management, cleaning, guest services and more.");
  const [params] = useSearchParams();
  const preset = params.get("service");
  const [form, setForm] = useState({ name: "", email: "", service: preset && preset !== "blueprint" ? preset : preset === "blueprint" ? "The Bermuda Vacation Rental Blueprint" : "", rating: 0, text: "", display_permission: true });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [reviews, setReviews] = useState([]);

  const load = () => api.get("/reviews").then((res) => setReviews(res.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.rating) return toast.error("Please choose a star rating.");
    setLoading(true);
    try {
      await api.post("/reviews", form);
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <main>
      <section className="mx-auto max-w-[1100px] px-6 pb-24 pt-40 lg:px-10">
        <SectionHead
          overline="Client Reviews"
          title={<>Words from owners<br /><span className="italic text-bermuda-dark">& guests.</span></>}
          copy="Worked with us? Your honest review helps the next homeowner or guest choose with confidence. Reviews appear after a quick check on our side."
        />

        <div className="mt-14 grid gap-12 lg:grid-cols-2 lg:gap-16">
          <Reveal>
            <div className="border border-navy/10 bg-white p-8 shadow-xl shadow-navy/5 lg:p-10">
              {done ? (
                <div className="py-10 text-center" data-testid="review-success">
                  <CheckCircle2 size={40} strokeWidth={1.2} className="mx-auto text-bermuda-dark" />
                  <h3 className="mt-6 font-serif text-3xl text-navy">Thank you.</h3>
                  <p className="mx-auto mt-4 max-w-sm text-sm leading-relaxed text-navy/60">
                    Your review has been received and will appear here once approved — usually quickly.
                  </p>
                </div>
              ) : (
                <>
                  <h3 className="font-serif text-2xl text-navy">Leave a Review</h3>
                  <form onSubmit={onSubmit} className="mt-7 grid gap-5" data-testid="review-form">
                    <div className="grid gap-5 sm:grid-cols-2">
                      <Field label="Your Name" required><Input data-testid="review-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Shown with your review" /></Field>
                      <Field label="Email" hint="Never shown publicly"><Input data-testid="review-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
                    </div>
                    <Field label="Which Service?" required>
                      <select data-testid="review-service" required value={form.service} onChange={(e) => setForm({ ...form, service: e.target.value })} className="w-full appearance-none border border-navy/15 bg-white px-4 py-3.5 text-sm text-navy outline-none focus:border-bermuda-dark">
                        <option value="">Select…</option>
                        {REVIEW_SERVICES.map((s) => <option key={s}>{s}</option>)}
                      </select>
                    </Field>
                    <div>
                      <span className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.16em] text-navy/70">Your Rating <span className="text-bermuda-dark">*</span></span>
                      <div className="flex gap-1.5" role="radiogroup" aria-label="Star rating">
                        {[1, 2, 3, 4, 5].map((s) => (
                          <button
                            type="button"
                            key={s}
                            role="radio"
                            aria-checked={form.rating === s}
                            aria-label={`${s} star${s > 1 ? "s" : ""}`}
                            onClick={() => setForm({ ...form, rating: s })}
                            data-testid={`review-star-${s}`}
                            className="-m-1.5 p-2.5 transition-transform hover:scale-110 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#C48B7F]"
                          >
                            <Star size={26} className={s <= form.rating ? "fill-bermuda-dark text-bermuda-dark" : "text-navy/25"} />
                          </button>
                        ))}
                      </div>
                    </div>
                    <Field label="Your Review" required><Textarea data-testid="review-text" required value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} placeholder="What did we do well? What should others know?" /></Field>
                    <label className="flex items-start gap-3 text-sm text-navy/70">
                      <input type="checkbox" data-testid="review-permission" checked={form.display_permission} onChange={(e) => setForm({ ...form, display_permission: e.target.checked })} className="mt-1 accent-[#0F1C2E]" />
                      I'm happy for this review to be displayed on {BRAND.name}'s website.
                    </label>
                    <SubmitButton loading={loading} testid="review-submit">Submit My Review</SubmitButton>
                  </form>
                </>
              )}
            </div>
          </Reveal>

          <Reveal delay={0.12}>
            <div data-testid="reviews-list">
              <h3 className="font-serif text-2xl text-navy">Recent Reviews</h3>
              {reviews.length === 0 ? (
                <div className="mt-7 border border-dashed border-navy/20 bg-sand-light/60 p-10 text-center" data-testid="reviews-empty">
                  <Quote size={22} strokeWidth={1.2} className="mx-auto text-navy/25" />
                  <p className="mt-4 text-sm text-navy/50">The first words are waiting to be written — perhaps yours.</p>
                </div>
              ) : (
                <div className="mt-7 space-y-5">
                  {reviews.map((r) => (
                    <div key={r.id} className="border border-navy/10 bg-white p-6" data-testid={`review-card-${r.id}`}>
                      <div className="flex items-center justify-between gap-3">
                        <Stars value={r.rating} />
                        <span className="text-[10px] uppercase tracking-[0.18em] text-navy/45">{r.service}</span>
                      </div>
                      <p className="mt-4 text-sm leading-relaxed text-navy/75">"{r.text}"</p>
                      <p className="mt-3 text-xs font-medium text-navy/55">— {r.name}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
